import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { SegmentationItem, TranslationMemorySegmentRegex } from "./types";
import { NotificationAppearance } from "../api/djangoToastSlice";
import { store } from "../utils/store";
import { setDjangoToastOpen } from "../api/djangoToastSlice";
import { useSegmentationMutation } from "../api/CATViewSlice";
import { uuidv4 } from "../utils/uuidUtils";
import debounce from "lodash/debounce";

type UseSegmentationItemsReturn = {
  items: SegmentationItem[];
  getAtIndex: (index: number) => SegmentationItem | undefined;
  setAtIndex: (index: number, updates: Partial<SegmentationItem>) => void;
  add: (
    segmentedText: Omit<SegmentationItem, "displaySource" | "displayTarget">
  ) => void;
  remove: (index: number) => void;
  length: number;
  isSegmenting: boolean;
  segmentFromRegex: (
    regex: TranslationMemorySegmentRegex,
    sourceResource: string,
    targetResource?: string,
    force?: boolean // If true, will force a re-segmentation even if the items are already segmented
  ) => void;

  isLockedAtIndex: (index: number) => boolean;
  toggleLockAtIndex: (index: number) => boolean;
  focusHandler: {
    index: number;
    setIndex: (index: number) => void;
    segment: () => SegmentationItem | undefined;
  };
  counts: {
    words: { source: number; target: number };
    characters: { source: number; target: number };
    segments: number;
  };
  setMultiple: (
    updates: { index: number; updates: Partial<SegmentationItem> }[]
  ) => void;
};

/**
 * A hook that manages a list of Segments.
 * @returns An object containing the segments, and functions to add, remove, and update them.
 */
export const useSegmentation = (
  jobId?: number,
  controlInputId?: string,
  noTranslateCells: {
    row: number;
    column: number;
  }[] = []
): UseSegmentationItemsReturn => {
  const [segmentation, { isLoading: isSegmenting }] = useSegmentationMutation();
  const [items, setStateItems] = useState<SegmentationItem[]>([]);
  const mounted = useRef<boolean>(true);

  const debounceRefs = useRef<{
    [key: number]: ReturnType<typeof debounce> | null;
  }>({});

  const getAtIndex = useCallback(
    (index: number): SegmentationItem | undefined => {
      try {
        return items[index];
      } catch (error) {
        console.error(error);
        return undefined;
      }
    },
    [items]
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      Object.values(debounceRefs.current).forEach((debounceRef) => {
        debounceRef?.cancel();
      });
      debounceRefs.current = {};
    };
  }, []);

  const setItems: typeof setStateItems = useCallback(
    (
      newState:
        | SegmentationItem[]
        | ((prev: SegmentationItem[]) => SegmentationItem[])
    ) => {
      if (!mounted.current) return;
      setStateItems((prevItems) => {
        const newItems =
          typeof newState === "function" ? newState(prevItems) : newState;
        if (controlInput.current) {
          controlInput.current.value = JSON.stringify(newItems);
        }
        return newItems;
      });
    },
    []
  );

  const controlInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (controlInputId) {
      const input = document.getElementById(controlInputId);
      if (input) {
        controlInput.current = input as HTMLInputElement;
      }
    }
  }, [controlInputId]);

  const setMultiple = useCallback(
    (
      updates: { index: number; updates: Partial<SegmentationItem> }[]
    ): void => {
      setItems((prevItems) => {
        const newItems = [...prevItems];
        updates.forEach(({ index, updates: segUpdates }) => {
          newItems[index] = { ...newItems[index], ...segUpdates };
        });
        return newItems;
      });
    },
    [setItems]
  );

  const setAtIndex = useCallback(
    (index: number, updates: Partial<SegmentationItem>): void => {
      try {
        if (
          isLockedAtIndex(index) &&
          Object.keys(updates).includes("target_text")
        ) {
          store.dispatch(
            setDjangoToastOpen({
              content: "Forbidden to edit",
              additionalContent: `Segment ${index + 1} is locked`,
              appearance: NotificationAppearance.WARNING,
            })
          );
          return;
        }
        setItems((prev) => {
          const newTexts = [...prev];
          newTexts[index] = { ...newTexts[index], ...updates };
          return newTexts;
        });
      } catch (error) {
        console.error(error);
      }
    },
    [getAtIndex]
  );

  const isLockedAtIndex = useCallback(
    (index: number): boolean => {
      return getAtIndex(index)?.locked || false;
    },
    [getAtIndex]
  );

  const toggleLockAtIndex = useCallback(
    (index: number): boolean => {
      const locked = !isLockedAtIndex(index);
      setAtIndex(index, { locked });
      return locked;
    },
    [isLockedAtIndex, setAtIndex]
  );

  const add = useCallback(
    (
      segmentedText: Omit<SegmentationItem, "displaySource" | "displayTarget">
    ): void => {
      setItems((prev) => [
        ...prev,
        {
          ...segmentedText,
        },
      ]);
    },
    []
  );

  const remove = useCallback((index: number): void => {
    try {
      setItems((prev) => {
        const newTexts = [...prev];
        newTexts.splice(index, 1);
        return newTexts;
      });
    } catch (error) {
      console.error(error);
    }
  }, []);

  const segmentFromRegex = useCallback(
    async (
      regex: TranslationMemorySegmentRegex,
      sourceResource: string,
      targetResource?: string,
      force?: boolean
    ): Promise<void> => {
      if (!regex) {
        return;
      }
      try {
        const segments = await segmentation({
          regex: regex.regex,
          source_resource: sourceResource,
          target_resource: targetResource,
          job_id: jobId,
          force,
        }).unwrap();
        let displayIndex = 0;
        const segmentedItems = segments.map<SegmentationItem>((segment) => {
          const addDisplayIndex = !!segment.source_text.trim();
          if (addDisplayIndex) {
            displayIndex = displayIndex + 1;
          }
          let locked = segment?.locked || false;
          let targetText = null;
          if (!locked && segment?.file_info) {
            const shouldBeLocked = noTranslateCells.some(
              (noTranslateCell) =>
                noTranslateCell.column === segment.file_info.column &&
                noTranslateCell.row === segment.file_info.row
            );
            locked = shouldBeLocked;
            if (
              !segment?.target_text &&
              segment.source_text &&
              shouldBeLocked
            ) {
              targetText = segment.source_text;
            }
          }
          return {
            ...segment,
            target_text: targetText || segment?.target_text,
            locked: locked,
            translation_memory_id: segment?.translation_memory_id || null,
            source_words: getWords(segment.source_text),
            display_index: addDisplayIndex ? displayIndex : undefined,
            uuid: uuidv4(),
          };
        });
        setItems(segmentedItems);
      } catch (error) {
        console.error(error);
        store.dispatch(
          setDjangoToastOpen({
            content: "Failed to segment text",
            appearance: NotificationAppearance.ERROR,
          })
        );
      }
    },
    [items, setItems, jobId]
  );

  // Focus handler
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const focusedSegment = useCallback((): SegmentationItem | undefined => {
    return getAtIndex(focusedIndex);
  }, [focusedIndex, getAtIndex]);

  const getWords = useCallback((text: string): number => {
    const trimmed = text.trim();
    if (trimmed === "") return 0;
    return trimmed.split(/\s+/).length;
  }, []);

  // Counts
  const words = useMemo((): { source: number; target: number } => {
    return items.reduce(
      (acc, item) => {
        acc.source += getWords(item.source_text);
        if (item.target_text) {
          acc.target += getWords(item.target_text);
        }
        return acc;
      },
      { source: 0, target: 0 }
    );
  }, [items]);

  const characters = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc.source += item.source_text.split("").length;
        if (item.target_text) {
          acc.target += item.target_text.split("").length;
        }
        return acc;
      },
      { source: 0, target: 0 }
    );
  }, [items]);

  return {
    items,
    isSegmenting: isSegmenting,
    getAtIndex,
    setAtIndex,
    add,
    remove,
    length: items.length,
    segmentFromRegex,
    isLockedAtIndex,
    toggleLockAtIndex,
    focusHandler: {
      index: focusedIndex,
      setIndex: setFocusedIndex,
      segment: focusedSegment,
    },
    counts: {
      words,
      characters,
      segments: items.length,
    },
    setMultiple,
  };
};
